/**
 * The org-unit scope tree — `employees-main.png`'s left card, and the picker
 * anywhere something is limited to part of the company.
 *
 * Checking a parent grants every unit beneath it, and a parent whose children
 * are only partly granted shows indeterminate. That is not decoration: the
 * server expands a grant downward (addendum §C.4), so a checked district
 * really does reach its branches, and a tree that drew them unchecked would be
 * telling somebody the opposite of what the API does.
 *
 * It has to survive a company with hundreds of branches, which a fully
 * expanded list does not. So: everything below the top level starts collapsed,
 * a search box filters and opens what it finds, and every node says how many
 * branches are under it and how many of those are on.
 */
import { useMemo, useState } from 'react'
import { Button, Checkbox } from './primitives.js'
import { IconCheck, IconSearch } from '../frame/icons.js'
import { cn } from '../lib/cn.js'

export interface OrgUnit {
  id: string
  kind: 'company' | 'region' | 'district' | 'branch'
  name: string
  parentId: string | null
}

const KIND_LABEL: Record<OrgUnit['kind'], string> = {
  company: '',
  region: 'Region · ',
  district: 'District · ',
  branch: '',
}

interface Node extends OrgUnit {
  children: Node[]
  /** Branches at or below this node, which is what "9 branches" counts. */
  branchCount: number
}

function buildTree(units: OrgUnit[]): Node[] {
  const byId = new Map<string, Node>(
    units.map((unit) => [unit.id, { ...unit, children: [], branchCount: 0 }]))
  const roots: Node[] = []

  for (const node of byId.values()) {
    const parent = node.parentId ? byId.get(node.parentId) : undefined
    if (parent) parent.children.push(node)
    else roots.push(node)
  }

  const count = (node: Node): number => {
    node.children.sort((a, b) => a.name.localeCompare(b.name))
    node.branchCount = (node.kind === 'branch' ? 1 : 0)
      + node.children.reduce((total, child) => total + count(child), 0)
    return node.branchCount
  }
  for (const root of roots) count(root)

  return roots.sort((a, b) => a.name.localeCompare(b.name))
}

const descendants = (node: Node): string[] =>
  [node.id, ...node.children.flatMap(descendants)]

/** The ids to keep when searching: a match, plus every ancestor of one. */
function matching(roots: Node[], term: string): Set<string> | null {
  const needle = term.trim().toLowerCase()
  if (!needle) return null

  const keep = new Set<string>()
  const walk = (node: Node, ancestors: string[]): boolean => {
    const self = node.name.toLowerCase().includes(needle)
    // A match keeps its whole subtree, so ticking the district it is under
    // still reads correctly.
    const below = node.children.map((child) => walk(child, [...ancestors, node.id]))
      .some(Boolean)
    if (self || below) {
      keep.add(node.id)
      for (const id of ancestors) keep.add(id)
      if (self) for (const id of descendants(node)) keep.add(id)
    }
    return self || below
  }
  for (const root of roots) walk(root, [])
  return keep
}

export function ScopeTree({
  units, granted, onChange, disabled,
}: {
  units: OrgUnit[]
  granted: string[]
  onChange?: (next: string[]) => void
  disabled?: boolean
}) {
  const roots = useMemo(() => buildTree(units), [units])
  const held = useMemo(() => new Set(granted), [granted])
  const [search, setSearch] = useState('')
  const [opened, setOpened] = useState<Set<string>>(new Set())

  const visible = useMemo(() => matching(roots, search), [roots, search])

  // Past a couple of dozen units the tree is a wall, so it starts folded and
  // a search opens what it finds. A small company sees everything at once,
  // which is what a small company wants.
  const big = units.length > 24
  const isOpen = (node: Node) =>
    Boolean(search.trim()) || opened.has(node.id) || (!big && node.kind !== 'branch')

  const toggleOpen = (node: Node) => setOpened((current) => {
    const next = new Set(current)
    if (next.has(node.id)) next.delete(node.id)
    else next.add(node.id)
    return next
  })

  function toggle(node: Node, next: boolean) {
    if (!onChange) return
    const affected = descendants(node)
    const result = new Set(held)
    for (const id of affected) {
      if (next) result.add(id)
      else result.delete(id)
    }
    onChange([...result])
  }

  const everything = useMemo(() => roots.flatMap(descendants), [roots])

  return (
    <div className="flex flex-col gap-2">
      {big && (
        <div className="flex items-center gap-2">
          <label className="relative flex h-8 min-w-0 flex-1 items-center">
            <span className="sr-only">Search branches</span>
            <span className="pointer-events-none absolute left-2 text-text-muted">
              <IconSearch size={14} />
            </span>
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search branches, districts, regions"
              disabled={disabled}
              className="h-8 w-full rounded-field border border-line bg-surface pl-7 pr-2 text-cell text-text placeholder:text-text-faint"
            />
          </label>
          {onChange && (
            <>
              <Button variant="ghost" disabled={disabled}
                onClick={() => onChange(everything)}>
                All
              </Button>
              <Button variant="ghost" disabled={disabled} onClick={() => onChange([])}>
                None
              </Button>
            </>
          )}
        </div>
      )}

      <div className={cn('flex flex-col gap-0.5', big && 'max-h-[280px] overflow-y-auto')}>
        {roots.map((root) => <Row key={root.id} node={root} depth={0} />)}
        {visible && visible.size === 0 && (
          <p className="m-0 px-1 py-3 text-center text-cell text-text-muted">
            Nothing matches that.
          </p>
        )}
      </div>
    </div>
  )

  function Row({ node, depth }: { node: Node; depth: number }) {
    if (visible && !visible.has(node.id)) return null

    const all = descendants(node)
    const checkedCount = all.filter((id) => held.has(id)).length
    const checked = held.has(node.id)
    // Indeterminate means "some of what is beneath this is granted" — the
    // state that tells somebody a district is partly covered.
    const indeterminate = !checked && checkedCount > 0
    const open = isOpen(node)
    const foldable = node.children.length > 0 && !search.trim()

    return (
      <>
        <div
          className="flex items-center gap-1.5 py-1"
          style={{ paddingLeft: depth * 18 }}
        >
          {foldable
            ? (
              <button
                type="button"
                aria-label={`${open ? 'Collapse' : 'Expand'} ${node.name}`}
                aria-expanded={open}
                onClick={() => toggleOpen(node)}
                className="w-3.5 shrink-0 text-cell text-text-muted hover:text-text"
              >
                {open ? '⌄' : '›'}
              </button>
            )
            : <span className="w-3.5 shrink-0" />}

          <Checkbox
            checked={checked}
            indeterminate={indeterminate}
            disabled={disabled}
            onChange={(event) => toggle(node, event.currentTarget.checked)}
            label={`${KIND_LABEL[node.kind]}${node.name}`}
          />

          {node.kind !== 'branch' && node.branchCount > 0 && (
            <span className={cn('ml-auto shrink-0 text-cell',
              checkedCount > 0 ? 'text-text-muted' : 'text-text-faint')}>
              {/* How much of it is on, so a folded district still answers. */}
              {checked || checkedCount === all.length
                ? `${node.branchCount} ${node.branchCount === 1 ? 'branch' : 'branches'}`
                : checkedCount > 0
                  ? `${branchesOn(node)} of ${node.branchCount}`
                  : `${node.branchCount} ${node.branchCount === 1 ? 'branch' : 'branches'}`}
            </span>
          )}
        </div>
        {open && node.children.map((child) => (
          <Row key={child.id} node={child} depth={depth + 1} />
        ))}
      </>
    )
  }

  function branchesOn(node: Node): number {
    const inherited = (current: Node, granted: boolean): number => {
      const on = granted || held.has(current.id)
      if (current.kind === 'branch') return on ? 1 : 0
      return current.children.reduce((total, child) => total + inherited(child, on), 0)
    }
    return inherited(node, false)
  }
}

/**
 * The whole question, not just the tree: is this everywhere, or only in some
 * of the company?
 *
 * Everywhere is the normal answer and the default, and a company with four
 * hundred branches should not have to tick four hundred boxes to say so — nor
 * find that an item added to a new branch next month is quietly missing from
 * it. So "everywhere" is an empty set on purpose, and the tree only appears
 * once somebody says otherwise.
 */
export function OrgUnitPicker({
  units, selected, onChange, label, everywhereHint, disabled, variant = 'segmented',
}: {
  units: OrgUnit[]
  selected: string[]
  onChange: (next: string[]) => void
  label: string
  /** One line on what "everywhere" means here. */
  everywhereHint: string
  disabled?: boolean
  /**
   * `chips` is the record-form shape (`record-flyouts.md` §4.1): one green
   * chip reading "All 5 branches" beside a "+ Limit to a branch" link, rather
   * than a segmented control offering a choice nobody makes twice a year.
   *
   * Same component and the same tree behind it — what changes is that the
   * normal answer is stated rather than selected, and narrowing is an action
   * you take rather than a mode you switch into.
   */
  variant?: 'segmented' | 'chips'
}) {
  /*
   * Mode is held rather than derived. "Some of the company" with nothing
   * ticked yet is a real state — it is what somebody sees the moment they
   * choose it — and deriving it from an empty set would bounce them straight
   * back to Everywhere.
   */
  const [scoped, setScoped] = useState(selected.length > 0)
  const everywhere = !scoped
  const branches = useMemo(() => {
    const byId = new Map(units.map((unit) => [unit.id, unit]))
    return selected.filter((id) => byId.get(id)?.kind === 'branch').length
  }, [units, selected])
  const allBranches = useMemo(
    () => units.filter((unit) => unit.kind === 'branch').length, [units])

  return (
    <div className="flex min-w-0 flex-col gap-[5px]">
      <span className={variant === 'chips'
        ? 'text-fieldlabel text-text-muted'
        : 'text-sub text-text-muted'}>
        {label}
      </span>

      {variant === 'chips'
        ? (
          <div className="flex flex-wrap items-center gap-2">
            <span className={cn('inline-flex h-7 items-center gap-1.5 rounded-chip border px-2.5',
              'text-[12.5px] font-semibold',
              everywhere
                ? 'border-accent-line bg-accent-soft text-accent-dark'
                : 'border-line bg-surface text-text-body')}>
              {everywhere && <IconCheck size={11} />}
              {everywhere
                ? allBranches > 0 ? `All ${allBranches} branches` : 'Everywhere'
                : branches === 0
                  ? 'Nowhere yet'
                  : `${branches} branch${branches === 1 ? '' : 'es'}`}
            </span>
            <button
              type="button"
              disabled={disabled}
              onClick={() => {
                setScoped(everywhere)
                if (!everywhere) onChange([])
              }}
              className="text-[12.5px] font-semibold text-accent hover:text-accent-dark"
            >
              {everywhere ? '+ Limit to a branch' : 'Use every branch'}
            </button>
          </div>
        )
        : (
          <div className="inline-flex gap-0.5 self-start rounded-button bg-segmented-track p-0.5">
            {[
              { value: true, label: 'Everywhere' },
              { value: false, label: 'Only some of the company' },
            ].map((option) => (
              <button
                key={String(option.value)}
                type="button"
                role="radio"
                aria-checked={everywhere === option.value}
                disabled={disabled}
                onClick={() => {
                  setScoped(!option.value)
                  // Switching to "some" leaves the tree empty and the hint
                  // below says so, rather than guessing at a selection.
                  if (option.value) onChange([])
                }}
                className={cn(
                  'rounded-[6px] px-2.5 py-1 text-[11.5px] font-semibold transition-colors',
                  everywhere === option.value
                    ? 'bg-surface text-text shadow-segmented'
                    : 'text-text-muted hover:text-text',
                )}
              >
                {option.label}
              </button>
            ))}
          </div>
        )}

      {everywhere
        ? <span className="text-[11px] leading-[1.4] text-text-faint">{everywhereHint}</span>
        : (
          <>
            <ScopeTree
              units={units}
              granted={selected}
              onChange={onChange}
              disabled={disabled}
            />
            <span className="text-[11px] leading-[1.4] text-text-faint">
              {branches === 0
                ? 'Nothing ticked yet, so this is available nowhere.'
                : `Available at ${branches} branch${branches === 1 ? '' : 'es'}.`}
            </span>
          </>
        )}
    </div>
  )
}
